import React, { useState, useEffect, useRef } from "react";
import { MdOutlineDone } from "react-icons/md";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowRight, FaArrowLeft } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./QuizForm.css";
import { api } from "../../config";

const StepBar = ({ currentStep, totalSteps }) => {
  const progress = (currentStep / totalSteps) * 100;
  return (
    <div className="stepbar-container">
      <span className="stepbar-label">
        Question {currentStep} / {totalSteps}
      </span>
      <div className="stepbar-bg">
        <div className="stepbar-progress" style={{ width: `${progress}%` }}></div>
      </div>
    </div>
  );
};

const QuizForm = () => {
  const [quizData, setQuizData] = useState([]);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(300);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [paginationStart, setPaginationStart] = useState(0);
  const [quizInfo, setQuizInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryRules, setCategoryRules] = useState([]);
  const [reminderShown, setReminderShown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const formData = location.state?.formData;
  const questionsPerPage = 5;
  const questionRefs = useRef([]);
  const storedQuiz = localStorage.getItem("selectedQuiz");
  const quizDataFromStorage = JSON.parse(storedQuiz);
  const quizId = quizDataFromStorage?.id;

  const formatTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${mins}:${secs}`;
  };

  useEffect(() => {
    const token = localStorage.getItem("selectedQuiz");
    if (!token || !storedQuiz) {
      setError("⛔ Unauthorized access or no quiz selected.");
      setLoading(false);
      return;
    }

  if (!storedQuiz) {
    setError("No quiz selected. Redirecting...");
    setLoading(false);
    return;
  }
    axios
      .get(`${api.API_URL}api/quizeData-full/${quizId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (res.status === 200 && res.data) {
          const apiData = res.data;

          const categoryRulesMap = {};
           if (Array.isArray(apiData.category_data)) {
          apiData.category_data?.forEach((cat) => {
            categoryRulesMap[cat.category_id] = {
              min_correct: cat.min_correct,
              num_questions: cat.num_questions,
              name: cat.name || `Category ${cat.category_id}`
            };
          });
        }
          const transformedData = apiData.questions.map((q) => {
            let options = [];
            if (q.type === "mcq" && q.options) {
              try {
                let parsedOptions = typeof q.options === "string" ? JSON.parse(q.options) : q.options;
                options = Object.values(parsedOptions).map((opt) => opt.trim());
                console.log("options",options);
              } catch (err) {
                console.error("Error parsing options:", err);
              }
            }
            console.log(options);
            const rule = categoryRulesMap[q.category.id];
            return {
              question_id: q.question_id,
              question: q.text,
              options,
              correct_answer: q.correct_answer?.trim()?.toUpperCase(),
              type: q.type,
              category_id: q.category.id,
              category_name: q.category.name,
              rule,
            };
          });

          setQuizInfo({
            name: apiData.name,
            description: apiData.description,
            time_limit: apiData.time_limit,
          });

          setCategoryRules(apiData.category_data);
          setQuizData(transformedData);
          setTimeLeft((apiData.time_limit || 5) * 60);
          setCurrentStep(1);
          setLoading(false);
        } else {
          setError("API data format error.");
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Error fetching quiz:", err);
        setError("Failed to load quiz data.");
        setLoading(false);
      });
  }, [navigate]);

  useEffect(() => {
    if (timeLeft > 0) {
      if (timeLeft === 5 && !reminderShown) {
        toast.error("⏰ Only 5 seconds left! Submitting soon...", { autoClose: 3000 });
        setReminderShown(true);
      }

      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      handleAutoSubmit();
    }
  }, [timeLeft, reminderShown]);

  const getLetterForIndex = (index) => String.fromCharCode(65 + index);

  const handleSelect = (questionIndex, optionIndex) => {
    const selectedLetter = getLetterForIndex(optionIndex);
    setAnswers((prev) => {
      const newAnswers = { ...prev };
      newAnswers[questionIndex] = selectedLetter;
      return newAnswers;
    });
  };

  const handleNext = () => {
    if (currentQuestion < quizData.length - 1) {
      const nextIndex = currentQuestion + 1;
      setCurrentQuestion(nextIndex);
      setCurrentStep(nextIndex + 1);
      questionRefs.current[nextIndex]?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      const prevIndex = currentQuestion - 1;
      setCurrentQuestion(prevIndex);
      setCurrentStep(prevIndex + 1);
      questionRefs.current[prevIndex]?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const goToNextPageGroup = () => {
    if (paginationStart + questionsPerPage < quizData.length) {
      setPaginationStart(paginationStart + questionsPerPage);
    }
  };

  const goToPrevPageGroup = () => {
    if (paginationStart > 0) {
      setPaginationStart(paginationStart - questionsPerPage);
    }
  };

  const handlePagination = (index) => {
    setCurrentQuestion(index);
    setCurrentStep(index + 1);
    questionRefs.current[index]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleAutoSubmit = async () => {
    try {
      const resultSummary = evaluateQuizResults(quizData, answers, categoryRules);
      const data = {
        name: formData?.name,
        email: formData?.email,
        mobile: formData?.mobile,
        fordata: formData,
        quiz_id: quizId,
        total_questions: quizData.length,
        correct_answers: resultSummary.summary.reduce((acc, curr) => acc + curr.correctAnswers, 0),
        score: (resultSummary.summary.reduce((acc, curr) => acc + curr.correctAnswers, 0) / quizData.length) * 100,
        status: resultSummary.overall,
        rule_details: resultSummary.summary
      };

      const response = await fetch(`${api.API_URL}api/testResult`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        localStorage.removeItem("quizAccessToken");
        localStorage.removeItem("selectedQuiz");

        navigate("/candidate-form/results", {
          state: {
            resultSummary,
            isAutoSubmit: true
          }
        });
      } else {
        throw new Error("Failed to submit the result");
      }
    } catch (error) {
      console.error("Error auto-submitting quiz result:", error);
      toast.error("❌ Failed to submit quiz result");
    }
  };

  const handleSubmit = async (isAuto = false) => {
    try {
      const resultSummary = evaluateQuizResults(quizData, answers, categoryRules);

      if (!isAuto) toast.success("✅ Quiz submitted successfully!");

      const data = {
        name: formData?.name,
        email: formData?.email,
        mobile: formData?.mobile,
        fordata: formData,
        quiz_id: quizId,
        total_questions: quizData.length,
        correct_answers: resultSummary.summary.reduce((acc, curr) => acc + curr.correctAnswers, 0),
        min_correct: resultSummary.summary.map((category) => category.minCorrect),
        score: (resultSummary.summary.reduce((acc, curr) => acc + curr.correctAnswers, 0) / quizData.length) * 100,
        status: resultSummary.overall,
        rule_details: resultSummary.summary
      };

      const response = await fetch(`${api.API_URL}api/testResult`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        localStorage.removeItem("quizAccessToken");
        localStorage.removeItem("selectedQuiz");

        navigate("/candidate-form/results", {
          state: {
            resultSummary,
            isAutoSubmit: isAuto
          }
        });
      } else {
        throw new Error("Failed to submit the result");
      }
    } catch (error) {
      console.error("Error submitting quiz result:", error);
      toast.error("❌ Failed to submit quiz result");
    }
  };

  const evaluateQuizResults = (quizData, answers, categoryRules) => {
    const categoryResults = {};
    let allCategoriesPassed = true;
    const categoryRulesMap = {};
    categoryRules.forEach(rule => {
      categoryRulesMap[rule.category_id] = rule;
    });

    quizData.forEach((q, index) => {
      const categoryId = String(q.category_id);
      const selectedAnswer = answers[index];
      const correctAnswer = q.correct_answer;
      const isCorrect = selectedAnswer === correctAnswer;
      const rule = categoryRulesMap[categoryId];

      if (!categoryResults[categoryId]) {
        categoryResults[categoryId] = {
          category: q.category_name,
          totalQuestions: 0,
          correctAnswers: 0,
          minCorrect: rule?.min_correct || 0,
          num_questions: rule?.num_questions || 0,
        };
      }

      categoryResults[categoryId].totalQuestions++;
      if (isCorrect) {
        categoryResults[categoryId].correctAnswers++;
      }
    });

    const summary = Object.values(categoryResults).map((result) => {
      const passed = result.correctAnswers >= result.minCorrect;
      if (!passed) allCategoriesPassed = false;
      return {
        category: result.category,
        totalQuestions: result.totalQuestions,
        correctAnswers: result.correctAnswers,
        minCorrect: result.minCorrect,
        passed,
      };
    });

    return {
      summary,
      overall: allCategoriesPassed ? "Passed" : "Failed",
    };
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) return <div className="error">{error}</div>;

  return (
    <section className="quiz-section">
      <div className="quiz-container">
        <div className="timer-box">
          <div className="timer-circle">
            <span className="timer-time">{formatTime(timeLeft)}</span>
            <span className="timer-label">sec</span>
          </div>
          <div className="timer-text">Quiz <br /> Time Start</div>
        </div>

        <div ref={(el) => (questionRefs.current[currentQuestion] = el)} className="quiz-box">
          <div className="quiz-header">
            <h2 className="quiz-title">{quizInfo?.name || "Quiz"}</h2>
            <p className="quiz-subtitle">{quizInfo?.description || "Test your knowledge!"}</p>
          </div>

          <StepBar currentStep={currentStep} totalSteps={quizData.length} />
          <h3 className="question-text">{quizData[currentQuestion].question}</h3>

          <div className="options-wrapper">
            {quizData[currentQuestion].options.map((option, index) => {
              const letter = getLetterForIndex(index);
              const isSelected = answers[currentQuestion] === letter;
              return (
                <label
                  key={index}
                  className={`option-label ${isSelected ? "option-selected" : ""}`}
                  onClick={() => handleSelect(currentQuestion, index)}
                >
                  <input
                    type="radio"
                    name={`question-${currentQuestion}`}
                    value={letter}
                    checked={isSelected}
                    onChange={() => handleSelect(currentQuestion, index)}
                    className="option-radio"
                  />
                  <span className="option-text">{letter}. {option}</span>
                </label>
              );
            })}
          </div>

          <div className="pagination-controls">
            <button onClick={goToPrevPageGroup} disabled={paginationStart === 0} className="pagination-btn"><FaArrowLeft /></button>
            {Array.from({ length: questionsPerPage }).map((_, i) => {
              const pageIndex = paginationStart + i;
              return pageIndex < quizData.length ? (
                <button
                  key={pageIndex}
                  onClick={() => handlePagination(pageIndex)}
                  className={`pagination-number ${currentQuestion === pageIndex ? "active" : ""}`}
                >
                  {pageIndex + 1}
                </button>
              ) : null;
            })}
            <button
              onClick={goToNextPageGroup}
              disabled={paginationStart + questionsPerPage >= quizData.length}
              className="pagination-btn"
            >
              <FaArrowRight />
            </button>
          </div>

          <div className="nav-buttons">
            <button onClick={handlePrev} disabled={currentQuestion === 0} className={`nav-btn prev-btn ${currentQuestion === 0 ? "disabled" : ""}`}>
              <FaArrowLeft className="nav-icon" />
              Last Question
            </button>
            <button
              onClick={currentQuestion === quizData.length - 1 ? () => handleSubmit(false) : handleNext}
              className={`nav-btn next-btn ${currentQuestion === quizData.length - 1 ? "submit" : ""}`}
            >
              {currentQuestion === quizData.length - 1 ? "Submit" : "Next Question"}
              {currentQuestion < quizData.length - 1 && <FaArrowRight className="nav-icon" />}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuizForm;
